import React from 'react'
import { Stack, useTheme } from '@mui/material'
import { DataGridPro, GridColDef, GridSortModel } from '@mui/x-data-grid-pro'
import Box from '@mui/material/Box'
import { Icon, Text } from '@mxenabled/mxui'

import { ANALYTICS_EVENTS, useEvent, useGlobalCopyStore, useScreenSize } from '../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../core/constants'

import { DebtChartData } from '../../core/utils/DebtUtils'
import { getPriorityValue, priorityComparator } from '../utils/DebtsTable'
import { HeaderCell } from '../../trends/components/trendstable/HeaderCell'
import { PriorityCell } from './debtstable/PriorityCell'
import { AccountCell } from './debtstable/AccountCell'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface DebtsTableProps {
  sx?: any
  debts: DebtChartData[]
  hoveredDebtGuid: string
  onHoverRow: (debtGuid: string) => void
  onClickRow: () => void
}

export const DebtsTable: React.FC<DebtsTableProps> = ({
  sx = {},
  debts,
  hoveredDebtGuid,
  onHoverRow,
  onClickRow,
}) => {
  const theme = useTheme()
  const { onEvent } = useEvent()
  const { isMobile } = useScreenSize()
  const { debts: copy, common: commonCopy } = useGlobalCopyStore()

  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'priority', sort: 'asc' },
  ])

  const desktopColumns: GridColDef[] = [
    {
      field: 'priority',
      headerName: copy.table_column_priority,
      renderHeader: HeaderCell,
      renderCell: PriorityCell,
      sortComparator: priorityComparator,
      sortable: true,
      hideSortIcons: true,
      type: 'custom',
      align: 'left',
      headerAlign: 'left',
      minWidth: isMobile ? 67 : 72,
      flex: isMobile ? 0.2 : 0.4,
    },
    {
      field: 'account',
      headerName: copy.table_column_account,
      renderHeader: HeaderCell,
      renderCell: AccountCell,
      sortable: false,
      type: 'custom',
      align: 'left',
      headerAlign: 'left',
      minWidth: isMobile ? 180 : 225,
      flex: 1,
    },
    {
      field: 'balance',
      headerName: copy.table_column_balance,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {formatCurrency(params.value, '0,0')}
        </Text>
      ),
      sortable: true,
      hideSortIcons: isMobile,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      minWidth: isMobile ? 70 : 122,
      flex: isMobile ? 0.4 : 0.6,
    },
    {
      field: 'interest_rate',
      headerName: copy.details_interest_rate,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {typeof params.value === 'number' ? `${Number(params.value).toFixed(2)}%` : '---'}
        </Text>
      ),
      sortable: true,
      hideSortIcons: isMobile,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      minWidth: 122,
      flex: 0.6,
    },
    {
      field: 'projected_payoff_date',
      headerName: copy.table_column_payoff_date,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {params.value ? formatDate(params.value, DATE_FORMATS_INTL.MONTH_SHORT_YEAR) : '---'}
        </Text>
      ),
      sortable: true,
      hideSortIcons: isMobile,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      minWidth: 122,
      flex: 0.6,
    },
    {
      field: 'monthly_payment',
      headerName: copy.table_column_amount_due,
      renderHeader: HeaderCell,
      renderCell: (params) => (
        <Text color={theme.palette.text.primary} variant="body2">
          {typeof params.value === 'number' ? formatCurrency(params.value, '0,0') : '---'}
        </Text>
      ),
      sortable: true,
      hideSortIcons: isMobile,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      minWidth: 122,
      flex: 0.6,
    },
  ]

  const mobileColumns: GridColDef[] = [
    ...desktopColumns.slice(0, 3),
    {
      field: 'chevron',
      headerClassName: 'chevron-header',
      headerName: '',
      renderCell: () => (
        <Stack aria-label={commonCopy.see_details} role="button">
          <Icon name="chevron_right" />
        </Stack>
      ),
      sortable: false,
      hideSortIcons: true,
      type: 'custom',
      align: 'right',
      headerAlign: 'right',
      width: 20,
    },
  ]

  const rows = React.useMemo(() => {
    return debts.map((debt, idx) => ({
      ...debt,
      id: idx,
      priority: getPriorityValue(debt),
    }))
  }, [debts])

  const handleRowHover = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    const target = event.target as HTMLElement
    const rowEl = target.closest('[data-id]')
    const debtIdx = rowEl?.getAttribute('data-id')
    const hoveredDebt = debts[Number(debtIdx)]
    onHoverRow?.(hoveredDebt?.guid ?? '')
  }

  const onMouseEnterRow = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    // Only use hover on non-mobile devices
    if (!isMobile) {
      handleRowHover(event)
    }
  }

  const onRowInteraction = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    // For mobile devices, use touch/click to highlight row
    if (isMobile) {
      handleRowHover(event)
    }
  }

  const getRowIdByGuid = (guid: string) => {
    return rows.find((r) => r.guid === guid)?.id
  }

  return (
    <Box
      sx={{
        height: 'auto',
        width: '100%',
        mt: '15px',
        overflowX: 'auto',
        overflowY: 'visible',
        boxShadow: 'none',
        ...sx,
      }}
    >
      <DataGridPro
        columns={isMobile ? mobileColumns : desktopColumns}
        disableColumnFilter={true}
        disableColumnMenu={true}
        hideFooter={true}
        initialState={{ sorting: { sortModel } }}
        onRowClick={(params) => {
          onClickRow()
          onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_TABLE_ROW, { account_guid: params.row.guid })
        }}
        onSortModelChange={(newModel) => {
          setSortModel(newModel)
          onEvent(ANALYTICS_EVENTS.DEBTS_CLICK_TABLE_SORT)
        }}
        rows={rows}
        slotProps={{
          baseIconButton: { color: 'secondary', sx: { ml: 8 } },
          row: {
            onMouseEnter: onMouseEnterRow,
            onMouseLeave: () => !isMobile && onHoverRow?.(''),
            onTouchStart: onRowInteraction,
            onClick: isMobile ? onRowInteraction : undefined,
          },
        }}
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        sx={{
          '& .chevron-header': { display: 'none' },
          [`& .MuiDataGrid-row[data-id="${getRowIdByGuid(hoveredDebtGuid)}"]`]: {
            backgroundColor: theme.palette.mode === 'light' ? '#f8f9fb' : '#1f2329',
            filter: 'brightness(98%)',
          },
        }}
      />
    </Box>
  )
}
